import { Card, Descriptions, Row, Tag, Typography } from 'antd';

import React, { useEffect } from 'react';
import { FormatDate } from '../../../constants';
import { Helmet } from 'react-helmet';

interface TransactionCardDetails {
  amount: number;
  status: string;
  reason: string;
  transactionReference: {
    completedAt: string;
    referenceId: string;
  };
  updatedAt: string;
}

export interface AdhocTransaction {
  amount: number;
  reason: string;
  status: string;
  approval: {
    isApplicantApproved: boolean;
    isApplicantApprovalRequired: boolean;
    applicantRespondedAt: string;
  };
  transactionReference: {
    completedAt: string;
    referenceId: string;
    vendor: string;
    isSuccess: boolean;
  };
  updatedAt: string;
}

export interface Submission {
  amount: number;
  transactionReference: {
    completedAt: string;
    referenceId: string;
  };
}

export interface TransactionsProps {
  isCaseOrApplicant: string;
  submissionReason: string;
  data: {
    caseId: string;
    transactions: {
      submission: Submission;
      adhocTransactions: AdhocTransaction[];
    };
  };
}
export const Transactions: React.FC<TransactionsProps> = (props) => {
  const [totalReceived, setTotalReceived] = React.useState<number>(0);
  const [totalSent, setTotalSent] = React.useState<number>(0);
  const [sortedTransactions, setSortedTransactions] = React.useState<TransactionCardDetails[]>([]);

  useEffect(() => {
    let adhocTransactions = props.data.transactions?.adhocTransactions ?? [];
    // sort adhoc transactions to show the most recent transaction first
    if (adhocTransactions.length > 0) {
      adhocTransactions = [...adhocTransactions].sort((a, b) => {
        return new Date(b?.updatedAt).getTime() - new Date(a?.updatedAt).getTime();
      });
    }
    const submissionAmount = props.data.transactions.submission?.amount ?? 0;

    // calculate total sent and total received amounts from perspective of Intealth
    const { totalSent, totalReceived } = adhocTransactions
      .filter((transaction) => {
        const { approval } = transaction;
        const transactionRef = transaction.transactionReference;
        return !((approval && approval.isApplicantApprovalRequired === true && approval.isApplicantApproved !== true) || transactionRef.isSuccess === false);
      })
      .reduce(
        (acc, transaction) => {
          const amount = transaction.amount ?? 0;
          return {
            totalSent: acc.totalSent + (amount < 0 ? Math.abs(amount) : 0),
            totalReceived: acc.totalReceived + (amount > 0 ? amount : 0)
          };
        },
        { totalSent: 0, totalReceived: submissionAmount }
      );

    setTotalSent(totalSent);
    setTotalReceived(totalReceived);

    // group adhoc transactions with case submission transaction details to be rendered in the card list
    setSortedTransactions([
      ...adhocTransactions,
      ...(submissionAmount > 0
        ? [
            {
              amount: submissionAmount,
              status: 'COMPLETED',
              reason: props.submissionReason,
              updatedAt: props.data.transactions?.submission?.transactionReference?.completedAt,
              transactionReference: {
                completedAt: props.data.transactions?.submission?.transactionReference?.completedAt,
                referenceId: props.data.transactions?.submission?.transactionReference?.referenceId
              }
            }
          ]
        : [])
    ]);
  }, [props.data]);



  const transactionActivityDescription = (transac: AdhocTransaction) => {
    switch (transac.status) {
      case 'COMPLETED':
        return <Tag>{`Completed ${FormatDate(transac.transactionReference.completedAt)}`}</Tag>;
      case 'FAILED':
        return <Tag>{`Failed ${FormatDate(transac.updatedAt)}`}</Tag>;
      case 'REJECTED':
        return <Tag>{`Rejected ${FormatDate(transac.approval.applicantRespondedAt)}`}</Tag>;
      case 'PENDING':
        return <Tag>{`Pending ${FormatDate(transac.updatedAt)}`}</Tag>;
      default:
        return <Tag>{`Unknown Status ${FormatDate(transac.updatedAt)}`}</Tag>;
    }
  };

  return (
    <>
      <Helmet>
        <title>{props?.isCaseOrApplicant} - Transaction</title>
      </Helmet>
      <Card
        hoverable
        style={{
          width: '100%',
          height: '70%',
          marginTop: 20
        }}
      >
        <Typography.Title level={4}>Transaction Summary</Typography.Title>
        <Row gutter={[5, 5]}>
          <Descriptions title={undefined} layout="vertical" size="small" column={2} className="w-[800px]" style={{ marginLeft: 10 }}>
            <Descriptions.Item label="Total Received">US${totalReceived}</Descriptions.Item>
            <Descriptions.Item label="Total Sent">US${totalSent}</Descriptions.Item>
          </Descriptions>
        </Row>
        <Typography.Title style={{ paddingTop: 20 }} level={4}>
          Transactions
        </Typography.Title>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10',
            overflowY: 'auto',
            maxHeight: '300px'
          }}
        >
          {sortedTransactions.length > 0 ? (
            sortedTransactions.map((transaction, _) =>
              transaction ? (
                <Card
                  key={transaction.updatedAt}
                  bordered={true}
                  style={{
                    width: '100%',
                    height: '20%',
                    marginTop: 24
                  }}
                >
                  <Row gutter={[5, 5]}>
                    <Descriptions title={undefined} layout="vertical" size="small" column={2} className="w-[800px]">
                      <Descriptions.Item label="Date">{FormatDate(transaction.updatedAt)}</Descriptions.Item>
                      <Descriptions.Item label="Description">
                        {transaction.amount && transaction.amount < 0
                          ? `${transaction.reason} Refund US$${Math.abs(transaction.amount)}`
                          : `${transaction.reason} US$${transaction.amount}`}
                      </Descriptions.Item>
                      <Descriptions.Item label="Transaction ID">{transaction.transactionReference?.referenceId ?? 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Activity">{transactionActivityDescription(transaction as AdhocTransaction)}</Descriptions.Item>
                    </Descriptions>
                  </Row>
                </Card>
              ) : null
            )
          ) : (
            <p className="px-5">No transactions have been made for this application.</p>
          )}
        </div>
      </Card>
    </>
  );
};
